/**
 * Peer Group (migrada do Streamlit; equivalência em backend/tests_paginas/test_peer_group.py).
 *
 * Na URL: grupos (vírgula), janela (contada para trás a partir da data final; padrão
 * desde o início do fundo), data inicial (só no Personalizado) e final, fundos do
 * gráfico de performance e das estatísticas ("|", porque nomes de fundos podem ter
 * vírgula; vazio = seleção padrão do legado) e a janela do gráfico Retorno x PL
 * (`periodo`, nome mantido pelos links antigos). Cada seção vem de um endpoint próprio.
 */
import { Alert, Badge, Group, Loader, MultiSelect, Select, Tabs, Text } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { ErroApi, apiGet, type Envelope } from '../../api/cliente';
import { AvisosApi } from '../../components/AvisosApi';
import {
  BarraFiltros,
  CampoData,
  CampoPeriodo,
  GrupoFiltros,
  LARGURA,
} from '../../components/Filtros';
import { Grafico, type GraficoApi } from '../../components/Grafico';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { opcoesPeriodo, type OpcaoPeriodo } from '../../lib/periodos';
import { useFiltrosUrl } from '../../lib/url-state';

const CAMINHO = '/fundos/peer-group';
export const GRUPOS = [
  'Trinity',
  'Yield',
  'Phoenix',
  'Prospera',
  'FIDC',
  'Compass',
  'Nemesis',
  'Proteus',
  'Long Bias',
];
const PERIODOS = ['day', 'mtd', 'ytd', '1m', '3m', '6m', '12m', '24m', '36m', 'custom'];
const ROTULO_PERIODO: Record<string, string> = { day: 'Dia', custom: 'Período escolhido' };
// Usadas enquanto a primeira resposta não chega; a API devolve a lista definitiva.
const JANELAS = [
  ...opcoesPeriodo(['ytd', '1a', '2a', '3a', '5a']),
  { valor: 'max', rotulo: 'Desde o início' },
];

interface DadosBase {
  grupos: string[];
  fundos: string[];
  persevera: string | null;
  min_data: string;
  max_data: string;
  padrao_grafico: string[];
  padrao_estatisticas: string[];
  janelas: OpcaoPeriodo[];
  heatmap: GraficoApi & { altura: number };
  avisos: string[];
}

type ComAviso<T> = { aviso: string } | T;

const separar = (texto: string, sep: string) => (texto ? texto.split(sep) : []);

function mensagem(erro: unknown, padrao: string) {
  return erro instanceof ErroApi ? erro.message : padrao;
}

export function PeerGroup() {
  const [filtros, definir] = useFiltrosUrl({
    grupos: 'Nemesis',
    janela: 'max',
    inicio: '',
    fim: '',
    fundos: '',
    periodo: 'day',
    estatisticas: '',
  });
  const grupos = separar(filtros.grupos, ',');
  const periodo = {
    grupos,
    janela: filtros.janela,
    inicio: filtros.inicio || undefined,
    fim: filtros.fim || undefined,
  };
  const habilitado = grupos.length > 0;

  const base = useQuery({
    queryKey: ['fundos.peer.base', grupos],
    queryFn: ({ signal }) => apiGet<Envelope<DadosBase>>(`${CAMINHO}/base`, { grupos }, signal),
    enabled: habilitado,
  });
  const perf = useQuery({
    queryKey: ['fundos.peer.performance', periodo, filtros.fundos],
    queryFn: ({ signal }) =>
      apiGet<
        Envelope<ComAviso<{ inicio: string; fim: string; fundos: string[]; grafico: GraficoApi }>>
      >(`${CAMINHO}/performance`, { ...periodo, fundos: separar(filtros.fundos, '|') }, signal),
    enabled: habilitado,
    placeholderData: keepPreviousData,
  });
  const tabela = useQuery({
    queryKey: ['fundos.peer.tabela', periodo, filtros.periodo],
    queryFn: ({ signal }) =>
      apiGet<Envelope<ComAviso<{ tabela: TabelaApi; dispersao: GraficoApi }>>>(
        `${CAMINHO}/tabela`,
        { ...periodo, periodo: filtros.periodo },
        signal,
      ),
    enabled: habilitado,
    placeholderData: keepPreviousData,
  });
  const stats = useQuery({
    queryKey: ['fundos.peer.estatisticas', periodo, filtros.estatisticas],
    queryFn: ({ signal }) =>
      apiGet<
        Envelope<
          ComAviso<{
            fundos: string[];
            abas: { titulo: string; grafico?: GraficoApi; aviso?: string; legenda?: string }[];
          }>
        >
      >(
        `${CAMINHO}/estatisticas`,
        { ...periodo, fundos: separar(filtros.estatisticas, '|') },
        signal,
      ),
    enabled: habilitado,
    placeholderData: keepPreviousData,
  });

  const dados = base.data?.data;
  const p = perf.data?.data;
  const t = tabela.data?.data;
  const s = stats.data?.data;
  const carregando = [base, perf, tabela, stats].some((q) => q.isFetching);
  const opcoesFundos = dados?.fundos ?? [];
  // Datas efetivas (da janela ou limitadas aos dados) vêm da consulta de performance.
  const inicioEfetivo = p && 'inicio' in p ? p.inicio : undefined;
  const fim = filtros.fim || (p && 'fim' in p ? p.fim : '');

  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Peer Group"
        descricao="Fundos Persevera contra os pares de cada grupo: performance, ranks, patrimônio, risco e correlação."
        dadosAte={dados?.max_data}
        fonte="ANBIMA Feed (cotas e PL) · Fibery (pares)"
      >
        {dados && (
          <Group gap={6}>
            {dados.grupos.map((g) => (
              <Badge key={g} color="dourado" variant="light" radius="sm">
                {g}
              </Badge>
            ))}
          </Group>
        )}
      </CabecalhoPagina>
      <BarraFiltros carregando={carregando}>
        <GrupoFiltros titulo="Grupos">
          <MultiSelect
            label="Grupos de fundos"
            data={GRUPOS}
            value={grupos}
            // Trocar de grupo muda os fundos disponíveis: seleções voltam ao padrão.
            onChange={(v) => definir({ grupos: v.join(','), fundos: '', estatisticas: '' })}
            w={LARGURA.selecao}
          />
        </GrupoFiltros>
        <GrupoFiltros titulo="Período">
          <CampoPeriodo
            periodo={filtros.janela}
            inicio={filtros.inicio}
            inicioEfetivo={inicioEfetivo}
            opcoes={dados?.janelas ?? JANELAS}
            min={dados?.min_data}
            max={dados?.max_data}
            desabilitado={!dados}
            aoMudar={({ periodo: janela, inicio }) =>
              definir(janela ? { janela, inicio } : { inicio })
            }
          />
          <CampoData
            rotulo="Data final"
            valor={fim}
            min={dados?.min_data}
            max={dados?.max_data}
            desabilitado={!dados}
            aoMudar={(d) => definir({ fim: d })}
          />
        </GrupoFiltros>
        <GrupoFiltros titulo="Performance acumulada">
          <MultiSelect
            label="Fundos e benchmarks"
            searchable
            disabled={!dados}
            data={opcoesFundos}
            value={p && 'fundos' in p ? p.fundos : []}
            onChange={(v) => definir({ fundos: v.join('|') })}
            w={300}
            styles={{ pillsList: { maxHeight: 64, overflowY: 'auto' } }}
          />
        </GrupoFiltros>
        <GrupoFiltros titulo="Retorno x PL">
          <Select
            label="Janela do retorno"
            allowDeselect={false}
            data={PERIODOS.map((x) => ({ value: x, label: ROTULO_PERIODO[x] ?? x.toUpperCase() }))}
            value={filtros.periodo}
            onChange={(x) => x && definir({ periodo: x })}
            w={LARGURA.numero + 30}
          />
        </GrupoFiltros>
        <GrupoFiltros titulo="Estatísticas adicionais">
          <MultiSelect
            label="Fundos e benchmarks"
            searchable
            disabled={!dados}
            data={opcoesFundos}
            value={s && 'fundos' in s ? s.fundos : []}
            onChange={(v) => definir({ estatisticas: v.join('|') })}
            w={300}
            styles={{ pillsList: { maxHeight: 64, overflowY: 'auto' } }}
          />
        </GrupoFiltros>
      </BarraFiltros>

      {!habilitado && <Alert color="blue">Selecione pelo menos um grupo de fundos.</Alert>}
      {base.isError && (
        <Alert color="red">{mensagem(base.error, 'Não foi possível carregar os dados.')}</Alert>
      )}
      {habilitado && base.isPending && <Loader />}

      {dados && (
        <>
          <AvisosApi
            avisos={[
              dados.avisos,
              base.data?.meta.avisos,
              perf.data?.meta.avisos,
              tabela.data?.meta.avisos,
              stats.data?.meta.avisos,
            ]}
          />

          <Secao
            titulo="Performance acumulada"
            legenda="Retorno acumulado no período escolhido, em %."
          >
            {p &&
              ('aviso' in p ? (
                <Alert color="blue">{p.aviso}</Alert>
              ) : (
                <Grafico grafico={p.grafico} />
              ))}
          </Secao>

          <Secao
            titulo="Tabela de performance"
            legenda="Retornos em % com todas as janelas encerradas na data final. Fundo sem cota nessa data (até 2 dias úteis antes) fica em branco e fora do rank. Cores por quartil; linha do fundo Persevera em destaque."
          >
            {t &&
              ('aviso' in t ? (
                <Alert color="blue">{t.aviso}</Alert>
              ) : (
                <TabelaDados tabela={t.tabela} alturaAutomatica />
              ))}
          </Secao>

          {t && !('aviso' in t) && (
            <Secao
              titulo="Retorno x Patrimônio Líquido"
              legenda="Cada ponto é um fundo com cota na data final; janela do retorno escolhida em Retorno x PL na barra de filtros."
            >
              <Grafico grafico={t.dispersao} />
            </Secao>
          )}

          <Secao
            titulo="Estatísticas adicionais"
            legenda="Drawdown, volatilidade de 21 dias, patrimônio e correlação de 21 dias no período escolhido."
          >
            {s &&
              ('aviso' in s ? (
                <Alert color="blue">{s.aviso}</Alert>
              ) : (
                <Tabs defaultValue={s.abas[0]?.titulo} keepMounted={false} color="dourado">
                  <Tabs.List>
                    {s.abas.map((a) => (
                      <Tabs.Tab key={a.titulo} value={a.titulo}>
                        {a.titulo}
                      </Tabs.Tab>
                    ))}
                  </Tabs.List>
                  {s.abas.map((a) => (
                    <Tabs.Panel key={a.titulo} value={a.titulo} pt="md">
                      {a.legenda && (
                        <Text size="xs" c="dimmed" mb="xs">
                          {a.legenda}
                        </Text>
                      )}
                      {a.grafico ? (
                        <Grafico grafico={a.grafico} />
                      ) : (
                        <Alert color="blue">{a.aviso}</Alert>
                      )}
                    </Tabs.Panel>
                  ))}
                </Tabs>
              ))}
          </Secao>

          <Secao
            titulo="Correlação dos fundos"
            legenda="Retornos diários de todo o histórico disponível (não depende do período escolhido)."
          >
            <Grafico grafico={dados.heatmap} />
          </Secao>
        </>
      )}
    </Pagina>
  );
}
